import React from 'react';
import { Alert, Box, Divider, Typography, CircularProgress, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { alpha } from '@mui/material/styles';
import EventRepeatOutlinedIcon from '@mui/icons-material/EventRepeatOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import DetailsCard from '../../../ui/layout/DetailsCard';
import ActionButton from '../../../ui/primitives/ActionButton';
import Loader from '../../../ui/primitives/Loader';

const FILTER_LABELS = {
  last_30_days: <>Last <Box component="span" sx={{ fontWeight: 700, color: 'text.secondary' }}>30 days</Box></>,
  last_90_days: <>Last <Box component="span" sx={{ fontWeight: 700, color: 'text.secondary' }}>90 days</Box></>,
  last_6_months: <>Last <Box component="span" sx={{ fontWeight: 700, color: 'text.secondary' }}>6 months</Box></>,
  last_year: <>Last <Box component="span" sx={{ fontWeight: 700, color: 'text.secondary' }}>year</Box></>,
  custom: <>Selected <Box component="span" sx={{ fontWeight: 700, color: 'text.secondary' }}>date range</Box></>,
};

const METRIC_CONFIG = [
  {
    key: 'active_test_cycles',
    label: 'Active Test Cycles',
    icon: EventRepeatOutlinedIcon,
    getValue: (g) => g.active_test_cycles?.count,
  },
  {
    key: 'orphan_test_cycles',
    label: 'Orphan Test Cycle',
    icon: HourglassEmptyOutlinedIcon,
    getValue: (g) => g.orphan_test_cycles?.count,
    infoTooltip: 'Active test cycles with no new iterations or executions for over 15 days',

  },
  {
    key: 'open_defects',
    label: 'Open Defects',
    icon: ErrorOutlineOutlinedIcon,
    getValue: (defects) => defects?.total_open_defects ?? 0,
    infoTooltip: 'Showing issues across all linked Jira projects',
  },
  {
    key: 'defects_logged',
    label: 'Defects Logged',
    icon: BugReportOutlinedIcon,
    getValue: (defects) => defects?.total_defects ?? 0,
    getFilterType: (g) => g.defects_logged?.filter?.type,
    infoTooltip: 'Showing issues across all linked Jira projects',
  },
  {
    key: 'automation_backlog',
    label: 'Automation Backlog',
    icon: PendingActionsOutlinedIcon,
    getValue: (g) => g.automation_backlog?.count,
  },
  {
    key: 'in_review',
    label: 'In Review TCs',
    icon: RateReviewOutlinedIcon,
    getValue: (g) => g.in_review?.count,
  },
];

const AtAGlanceSection = ({
  data,
  defectsData,
  defectsLoading,
  defectsIsError,
  defectsError,
  loading,
  isError,
  error,
  refetch,
  sx = {},
}) => {
  const atAGlance = data?.at_a_glance || {};
  const defects = defectsData || {};

  if (loading) {
    return (
      <DetailsCard title="At a Glance" sx={sx}>
        <Loader minHeight={280} />
      </DetailsCard>
    );
  }

  if (isError) {
    return (
      <DetailsCard title="At a Glance" sx={sx}>
        <Alert severity="error" action={<ActionButton label="Retry" onClick={() => refetch()} />}>
          {error?.message || 'Failed to load at a glance metrics.'}
        </Alert>
      </DetailsCard>
    );
  }

  return (
    <DetailsCard
      title="At a Glance"
      sx={{ display: 'flex', flexDirection: 'column', ...sx }}
      contentSx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
    >
      {METRIC_CONFIG.map((config, index) => {
        const Icon = config.icon;
        const isDefectMetric = ['open_defects', 'defects_logged'].includes(config.key);
        const value = isDefectMetric ? defectsLoading ? null : defectsIsError
          ? 'Unable to fetch' : config.getValue(defects) : config.getValue(atAGlance);
        const filterType = config.getFilterType?.(isDefectMetric ? defects : atAGlance);
        const subtitle = filterType ? FILTER_LABELS[filterType] || FILTER_LABELS.custom : null;
        const isLast = index === METRIC_CONFIG.length - 1;

        return (
          <Box key={config.key}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                py: 1.75,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                <Box
                  sx={(theme) => ({
                    width: 32,
                    height: 32,
                    borderRadius: `${theme.shape.borderRadius}px`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    '& svg': { fontSize: 16 },
                  })}
                >
                  <Icon />
                </Box>
               <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2" color="text.primary" fontWeight={500} noWrap>
                    {config.label}
                  </Typography>
                  {config.infoTooltip && (
                    <Tooltip title={config.infoTooltip} arrow placement="top">
                      <InfoOutlinedIcon
                        sx={{ fontSize: 14, color: 'text.disabled', cursor: 'default', flexShrink: 0 }}
                      />
                    </Tooltip>
                  )}
                </Box>
                {subtitle && (
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{
                      display: 'block',
                      color: 'text.disabled',
                      fontSize: '0.6875rem',
                      lineHeight: 1.3,
                      mt: 0.25,
                    }}
                  >
                    {subtitle}
                  </Typography>
                )}
              </Box>
              </Box>

              {isDefectMetric && defectsLoading ? (
                <CircularProgress size={16} thickness={5} />
              ) : (
                <Typography
                  variant={defectsIsError && isDefectMetric ? 'caption' : config.compact ? 'body2' : 'subtitle1'}
                  fontWeight={defectsIsError && isDefectMetric ? 500 : 700}
                  color={defectsIsError && isDefectMetric ? 'text.secondary' : 'text.primary'}
                  whiteSpace="nowrap"
                  textAlign="right"
                >
                  {value ?? '--'}
                </Typography>
              )}
            </Box>

            {!isLast && <Divider />}
          </Box>
        );
      })}
    </DetailsCard>
  );
};

export default AtAGlanceSection;